import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Crown, Zap, Users, MessageSquare, Clock, Check, ArrowRight, Trophy } from "lucide-react";
import { toast } from "sonner";
import PinClaim from "@/components/site/PinClaim";
import ShareCard from "@/components/site/ShareCard";
import { StripeTrustBadge } from "@/components/site/StripeTrustBadge";
import { openCheckoutWithPromise } from "@/lib/checkout";

const PERKS = [
  {
    icon: <Users />,
    title: "The Lounge · members-only chat",
    body: "An open community chat room exclusively for Tour Pass holders. Hang with BooBoo and other members between stops — location-specific rooms light up as the Sentra rolls into new cities.",
  },
  {
    icon: <Clock />,
    title: "12-hour early access",
    body: "Every city's drop unlocks for you 12 hours before the public window opens. Cop the heat first.",
  },
  {
    icon: <MessageSquare />,
    title: "VIP AI channel",
    body: "Talk to the FMAA AI in a private channel — full road-log context, deeper stories, no rate limits.",
  },
  {
    icon: <Zap />,
    title: "Drop alerts first",
    body: "When the Sentra rolls into a new city, your phone lights up before the banner ships site-wide.",
  },
  {
    icon: <Crown />,
    title: "Tour Pass insignia",
    body: "VIP badge on your profile, in The Lounge, and at IRL meetups. They'll know.",
  },
];

export default function TourPassPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    api
      .get("/tour-pass/status")
      .then((r) => setStatus(r.data))
      .catch((err) => {
        console.warn("tour-pass status failed:", err?.response?.status || err?.message);
      });
  }, [user]);

  useEffect(() => {
    api
      .get("/tour-pass/leaderboard")
      .then((r) => setLeaderboard(r.data || []))
      .catch((err) => {
        console.warn("leaderboard failed:", err?.response?.status || err?.message);
      });
  }, []);

  const activate = async () => {
    if (!user) {
      navigate("/login?redirect=/tour-pass");
      return;
    }
    setLoading(true);
    try {
      await openCheckoutWithPromise(async () => {
        const { data } = await api.post("/tour-pass/checkout", {
          origin_url: window.location.origin,
        });
        return data.url;
      });
    } catch (err) {
      console.warn("tour-pass checkout failed:", err?.response?.status || err?.message);
      toast("Could not start checkout. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tour-pass-page" className="relative">
      {/* Hero */}
      <section className="relative grain splatter overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF1E1E]/25 via-transparent to-[#FFC107]/15 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-20 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <Crown className="text-[#FFC107]" size={20} />
            <span className="overline">FMAA · Tour Pass</span>
          </div>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.85] drip-shadow max-w-4xl">
            Worth
            <br />
            the <span className="text-[#FF1493]">stop.</span>
          </h1>
          <p className="text-white/75 max-w-2xl mt-7 text-lg">
            Tour Pass unlocks every city's drop 12 hours before the public, opens a private AI
            channel with FMAA, and stamps your profile with a VIP badge. <span className="text-white">$9 / 30 days.</span>
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <HeroCTA
              user={user}
              status={status}
              loading={loading}
              onActivate={activate}
            />
          </div>
          {!status?.active && (
            <div className="mt-4">
              <StripeTrustBadge variant="caption" className="justify-start" />
            </div>
          )}

          {status?.active && status?.expires_at && (
            <div className="mt-4 text-xs font-mono-tactical text-white/60 flex flex-wrap gap-x-6 gap-y-1">
              <span>
                Renews/expires:{" "}
                {new Date(status.expires_at).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {status?.member_number && (
                <span data-testid="tour-pass-member-number">
                  Member{" "}
                  <span className="text-white">#{String(status.member_number).padStart(3, "0")}</span>
                  {status.is_founding_member && (
                    <span className="text-[#FFC107]">{" · Founding Member"}</span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Founding Member pin claim */}
      <PinClaim />

      {/* Share card — any user with a member number (founding status is permanent) */}
      {status?.member_number && (
        <ShareCard
          memberNumber={status.member_number}
          isFounding={status.is_founding_member}
          displayName={user?.name || (user?.email || "").split("@")[0]}
        />
      )}

      {/* Leaderboard */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="overline flex items-center gap-2">
              <Trophy size={12} /> Top of the road
            </div>
            <h2 className="font-display text-4xl md:text-5xl uppercase mt-3">
              Longest-running members
            </h2>
          </div>
          <div className="text-xs font-mono-tactical text-white/50 uppercase tracking-[0.25em]">
            First 100 = Founding · gold for life
          </div>
        </div>

        <div className="border border-white/10 bg-[#141414]" data-testid="tour-pass-leaderboard">
          {leaderboard.length === 0 ? (
            <div className="p-8 text-white/60 text-sm">No members yet — be the first.</div>
          ) : (
            <ul>
              {leaderboard.map((row, i) => (
                <li
                  key={row.member_number}
                  className="flex items-center gap-4 px-5 md:px-7 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition"
                  data-testid={`leaderboard-row-${i + 1}`}
                >
                  <div className="font-display text-3xl md:text-4xl text-white/30 w-12 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-xl md:text-2xl uppercase">
                        {row.name_first}
                      </span>
                      {row.is_founding_member && (
                        <span className="bg-[#FFC107] text-[#0A0A0A] px-2 py-0.5 text-[10px] font-display tracking-[0.2em] flex items-center gap-1">
                          <Crown size={10} fill="#0A0A0A" /> FOUNDING · #
                          {String(row.member_number).padStart(3, "0")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs font-mono-tactical text-white/50 mt-1">
                      Member since{" "}
                      {new Date(row.first_activated_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-[#FF1493]">{row.days_active}</div>
                    <div className="text-[10px] font-mono-tactical uppercase tracking-[0.2em] text-white/50">
                      days on the road
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="text-xs font-mono-tactical text-white/40 mt-3 uppercase tracking-[0.25em]">
          Names anonymized to first-name only · Position updates daily
        </p>
      </section>

      {/* Perks */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="overline">What's inside</div>
        <h2 className="font-display text-4xl md:text-5xl uppercase mt-3">The perks</h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {PERKS.map((p) => (
            <div key={p.title} className="tac-card p-7" data-testid={`perk-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="text-[#FF1493]">{p.icon}</span>
              <h3 className="font-display text-2xl uppercase mt-5">{p.title}</h3>
              <p className="text-white/70 text-sm mt-3">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing card */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="border border-white/10 bg-[#141414] p-10 md:p-16 relative overflow-hidden splatter grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="overline">The deal</div>
            <h2 className="font-display text-5xl md:text-6xl uppercase mt-3 drip-shadow">
              $9 buys you 30 days of being first.
            </h2>
            <ul className="mt-8 space-y-3 text-white/80">
              {[
                "12h early access on every city drop",
                "Private VIP channel with FMAA AI",
                "Priority drop notifications",
                "VIP badge across the platform",
                "Cancel any time — no auto-renew until you say so",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-1 w-4 h-4 bg-[#FF1493] flex items-center justify-center shrink-0">
                    <Check size={11} />
                  </span>
                  <span className="text-sm">{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="border border-[#FF1493]/40 bg-[#0A0A0A] p-8 text-center">
              <div className="overline">Tour Pass</div>
              <div className="mt-3 flex items-end justify-center gap-2">
                <span className="font-display text-7xl leading-none">$9</span>
                <span className="text-white/60 mb-2">/ 30 days</span>
              </div>
              <PricingCardCTA
                user={user}
                status={status}
                loading={loading}
                onActivate={activate}
              />
              <div className="mt-4 flex justify-center">
                <StripeTrustBadge />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


function HeroCTA({ user, status, loading, onActivate }) {
  if (!user) {
    return (
      <Link to="/login?redirect=/tour-pass" className="btn-blaze" data-testid="tour-pass-login">
        <Crown size={14} /> Sign in to activate
      </Link>
    );
  }
  if (status?.active) {
    return (
      <div
        className="flex items-center gap-3 bg-[#FFC107] text-[#0A0A0A] px-5 py-3 font-display tracking-[0.18em]"
        data-testid="tour-pass-active-state"
      >
        <Check size={16} /> ACTIVE · {status.days_remaining} DAYS LEFT
      </div>
    );
  }
  const price = status?.price?.toFixed(2) ?? "9.00";
  const days = status?.days ?? 30;
  return (
    <button
      onClick={onActivate}
      disabled={loading}
      className="btn-blaze"
      data-testid="tour-pass-activate"
    >
      <Crown size={14} />
      {loading ? "Loading..." : `Activate · $${price} / ${days} days`}
      <ArrowRight size={14} />
    </button>
  );
}

function PricingCardCTA({ user, status, loading, onActivate }) {
  if (status?.active) {
    return (
      <div
        className="mt-6 text-[#FFC107] font-display tracking-[0.18em]"
        data-testid="tour-pass-card-active"
      >
        ACTIVE · {status.days_remaining}D LEFT
      </div>
    );
  }
  if (!user) {
    return (
      <Link
        to="/login?redirect=/tour-pass"
        className="btn-blaze w-full justify-center mt-6"
        data-testid="tour-pass-card-login"
      >
        <Crown size={14} /> Sign in to activate
      </Link>
    );
  }
  return (
    <button
      onClick={onActivate}
      disabled={loading}
      className="btn-blaze w-full justify-center mt-6"
      data-testid="tour-pass-activate-card"
    >
      <Crown size={14} />
      {loading ? "Loading..." : "Activate now"}
    </button>
  );
}
